import React, { useEffect, useState } from "react";
import { expensesApi, accountsApi } from "../api";
import { useI18n } from "../i18n";
import { useAccount } from "../contexts/AccountContext";
import { showToast } from "../utils/toast";

export default function ExpenseModal({ open, onClose, expenseId }) {
  const { t } = useI18n();
  const { selectedAccountId, viewMode } = useAccount();
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedMoneyAccountId, setSelectedMoneyAccountId] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ date: today, amount: "", category: "Other", frequency: "daily", note: "" });
  const [categories, setCategories] = useState(["Other", "Rent", "Electricity", "Wages", "Delivery", "Marketing"]);
  const [showCategoryList, setShowCategoryList] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [accs, cats] = await Promise.all([
          accountsApi.list().catch(()=>[]),
          expensesApi.listCategories().catch(()=>[]),
        ]);
        setAccounts(Array.isArray(accs) ? accs : []);
        if (Array.isArray(cats) && cats.length) setCategories(cats);
        else if (cats && typeof cats === 'object') {
          const defs = Array.isArray(cats.defaults) ? cats.defaults : [];
          const customs = Array.isArray(cats.customs) ? cats.customs : [];
          const uniq = Array.from(new Set([ ...defs, ...customs ]));
          if (uniq.length) setCategories(uniq);
        }
        if (expenseId) {
          const e = await expensesApi.detail(expenseId);
          setForm({
            date: (e?.date?._seconds) ? new Date(e.date._seconds * 1000).toISOString().split('T')[0] : today,
            amount: String(e?.amount || ''),
            category: e?.category || 'Other',
            frequency: e?.frequency || 'daily',
            note: e?.note || '',
          });
          try { setCategoryQuery(e?.category || ''); } catch {}
          setSelectedMoneyAccountId(e?.money_account_id || '');
        } else {
          setSelectedMoneyAccountId('');
          setForm({ date: today, amount: "", category: "Other", frequency: "daily", note: "" });
          setCategoryQuery('');
        }
      } catch {}
    })();
  }, [open, expenseId]);

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const payload = {
        date: form.date,
        amount: parseFloat(form.amount || 0),
        category: form.category,
        frequency: form.frequency,
        note: form.note,
        money_account_id: selectedMoneyAccountId === '' ? null : (selectedMoneyAccountId || undefined),
      };
      if (expenseId) await expensesApi.update(expenseId, payload);
      else await expensesApi.create(payload);
      if (onClose) onClose(true);
    } catch (e) {
      console.error('Failed to save expense', e);
      showToast({ message: String(e?.message || e), variant: 'error' });
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-green-900/10">
      <div className="w-full sm:w-[420px] bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-green-100 max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-green-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-800">{expenseId ? t('exp.modal.editTitle') : t('exp.add.addExpense')}</div>
          <button className="text-sm text-gray-500" onClick={()=>onClose && onClose(false)}>✕</button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2">
            <input className="border rounded px-2 py-1 text-sm" type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} />
            <input className="border rounded px-2 py-1 text-sm" type="number" placeholder={t('exp.add.amount')} value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} />
            <div className="relative">
              <input
                className="border rounded px-2 py-1 text-sm w-full"
                placeholder="Select or type category"
                value={categoryQuery}
                onChange={(e)=>{ setCategoryQuery(e.target.value); setShowCategoryList(true); }}
                onFocus={()=> setShowCategoryList(true)}
                onBlur={()=> setTimeout(()=> {
                  setShowCategoryList(false);
                  const q = String(categoryQuery||'').trim().toLowerCase();
                  const match = (categories||[]).find(c => String(c||'').toLowerCase() === q);
                  if (!match) {
                    setForm(prev => ({ ...prev, category: 'Other' }));
                    setCategoryQuery('');
                  } else {
                    setForm(prev => ({ ...prev, category: match }));
                  }
                }, 150)}
              />
              {showCategoryList && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-green-100 rounded-lg shadow-lg max-h-56 overflow-auto">
                  {(() => {
                    const q = String(categoryQuery||'').trim().toLowerCase();
                    const list = (categories||[]).filter(c => !q || String(c||'').toLowerCase().includes(q));
                    if (list.length === 0) {
                      return (
                        <div className="p-2 text-sm text-gray-600">
                          <div className="mb-2">No categories found</div>
                          <button className="text-green-700 underline" onMouseDown={(e)=> e.preventDefault()} onClick={()=>{ try { if (window && window.tpNavigate) window.tpNavigate('expenses'); } catch {}; if (onClose) onClose(false); }}>+ Add Category</button>
                        </div>
                      );
                    }
                    return list.map(c => (
                      <button
                        key={c}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-green-50"
                        onMouseDown={(e)=> e.preventDefault()}
                        onClick={()=>{ setForm(prev => ({...prev, category: c})); setCategoryQuery(c); setShowCategoryList(false); }}
                      >{c}</button>
                    ));
                  })()}
                </div>
              )}
            </div>
            <select className="border rounded px-2 py-1 text-sm" value={form.frequency} onChange={(e)=>setForm({...form, frequency:e.target.value})}>
              <option value="daily">{t('exp.freq.daily')}</option>
              <option value="monthly">{t('exp.freq.monthly')}</option>
            </select>
            <input className="border rounded px-2 py-1 text-sm col-span-2" placeholder={t('exp.add.note.placeholder')} value={form.note} onChange={(e)=>setForm({...form, note:e.target.value})} />
            <div className="col-span-2">
              <div className="text-xs text-gray-600 mb-1">Account</div>
              <select className="border rounded px-2 py-1 text-sm w-full" value={selectedMoneyAccountId} onChange={(e)=>setSelectedMoneyAccountId(e.target.value)}>
                <option value="">Select account (clears selection)</option>
                {(accounts || []).map((a) => (
                  <option key={a.accountId} value={a.accountId}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="p-4 flex gap-2 border-t border-green-100">
          <button className={`flex-1 bg-green-600 text-white rounded-xl py-2 text-sm shadow ${saving?'opacity-60':''}`} onClick={handleSave} disabled={saving}>{t('exp.modal.save')}</button>
          <button className="flex-1 bg-white border border-green-200 text-green-700 rounded-xl py-2 text-sm" onClick={()=>onClose && onClose(false)}>{t('exp.modal.cancel')}</button>
        </div>
      </div>
    </div>
  );
}


